import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { toast } from '../../lib/toast';
import { sortLocations } from '../../lib/deviceUiModel';

/**
 * LinkSwitchesDialog — the simple "make these switches work together" wizard.
 * Pick 2+ toggle switches → save. Writes ONE automation carrying a
 * `linkedSwitches` template (no conditions, no scenes); the hub engine keeps the
 * members in lockstep. Members are derived from each device's binary control —
 * no raw attribute keys shown.
 */
const API = '/api';

function titleCase(s) {
  return (s || 'Other').replace(/\b\w/g, (l) => l.toUpperCase());
}

// A switch is any device with a binary (on/off) control.
function binaryControlOf(device) {
  return device.controls?.find((c) => c.valueType === 'binary');
}

export default function LinkSwitchesDialog({ open, onOpenChange, automation, onSaved }) {
  const { apiRequest } = useAuth();
  const isEdit = !!automation;

  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [selected, setSelected] = useState(() => new Set());

  // Load devices + seed form each time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setName(automation?.name || '');
    setSelected(new Set((automation?.template?.config?.members || []).map((m) => m.deviceId)));

    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const { response, data } = await apiRequest(`${API}/devices`);
        if (!response.ok) throw new Error(`API error: ${response.status}`);
        if (!cancelled) setDevices(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!cancelled) toast.error('Failed to load devices');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, automation, apiRequest]);

  const switches = useMemo(() => devices.filter(binaryControlOf), [devices]);

  const groups = useMemo(() => {
    const map = new Map();
    for (const d of switches) {
      const key = d.location || 'Other';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(d);
    }
    return sortLocations([...map.keys()]).map((loc) => [loc, map.get(loc)]);
  }, [switches]);

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const canSave = selected.size >= 2 && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    const members = switches
      .filter((d) => selected.has(d.id))
      .map((d) => {
        const control = binaryControlOf(d);
        return {
          deviceId: d.id,
          attribute: control?.property,
          ...(d.parentId && d.parentId !== d.id ? { parentId: d.parentId } : {}),
        };
      });

    const payload = {
      name: name.trim() || 'Linked Switches',
      active: automation?.active ?? true,
      conditions: [],
      actions: [],
      conditionLogic: 'AND',
      triggerOnAnyDeviceUpdate: false,
      template: { type: 'linkedSwitches', config: { members } },
    };

    try {
      setSaving(true);
      const url = isEdit ? `${API}/automations/${automation.id}` : `${API}/automations`;
      const { response, data } = await apiRequest(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error(`API error: ${response.status}`);
      toast.success(isEdit ? 'Linked switches updated' : 'Switches linked');
      onSaved?.(data, isEdit);
      onOpenChange(false);
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="sheet" className="p-0">
        <div className="flex flex-col max-h-[75vh]">
          <DialogHeader className="p-6 pb-3">
            <DialogTitle>{isEdit ? 'Edit Linked Switches' : 'Link Switches'}</DialogTitle>
            <DialogDescription>
              Pick the switches that should work together. Flip any one and the rest follow.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-6 space-y-5">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Name (e.g. Staircase)"
            />

            {loading ? (
              <p className="text-sm text-muted-foreground py-4">Loading switches…</p>
            ) : switches.length < 2 ? (
              <p className="text-sm text-muted-foreground py-4">
                You need at least two switches to link. Add more switches first.
              </p>
            ) : (
              groups.map(([location, items]) => (
                <div key={location} className="space-y-2">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {titleCase(location)}
                  </p>
                  <div className="rounded-2xl border border-border/60 divide-y divide-border/60 overflow-hidden">
                    {items.map((d) => (
                      <button
                        key={d.id}
                        type="button"
                        onClick={() => toggle(d.id)}
                        className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-accent/50 transition-colors"
                      >
                        <span className="text-sm">{d.name}</span>
                        <Checkbox checked={selected.has(d.id)} onCheckedChange={() => toggle(d.id)} />
                      </button>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>

          <DialogFooter className="p-6 pt-4 border-t border-border/60">
            <Button onClick={handleSave} disabled={!canSave} className="w-full sm:w-auto">
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
